import { useMemo, useState } from 'react';
import { createFileRoute, redirect } from '@tanstack/react-router';
import {
  AlertCircle,
  Boxes,
  CalendarClock,
  Package,
  Plus,
  RefreshCw,
  Search,
  ShieldAlert,
  Wrench,
} from 'lucide-react';
import { useAuthStore } from '@/stores/auth.store';
import { Card, CardContent } from '@/components/atoms/card';
import { Badge } from '@/components/atoms/badge';
import { Button } from '@/components/atoms/button';
import { Input } from '@/components/atoms/input';
import { Skeleton } from '@/components/atoms/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/atoms/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/atoms/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/atoms/table';
import { cn } from '@/lib/utils';
import { formatDateOnly } from '@/lib/dates/date-only';
import { useHasPermission } from '@/hooks/usePermissions';
import { useContactAddresses } from '@/hooks/contacts/useContactAddresses';
import {
  useEquipmentCategories,
  useEquipmentInventoryReport,
  useEquipment,
  useEquipmentWarrantyReport,
  useMaintenanceOverdue,
  useReplacementPipelineReport,
  useWarrantyExpiring,
} from '@/hooks/equipment/useEquipment';
import { EquipmentDetailSheet } from '@/components/organisms/equipment/EquipmentDetailSheet';
import {
  EquipmentFormDialog,
  type EquipmentAddressOption,
} from '@/components/organisms/equipment/EquipmentFormDialog';
import { type CustomerSelectorContactResult } from '@/components/organisms/CustomerSelector';
import { CompanyLocationSelector } from '@/components/organisms/CompanyLocationSelector';
import {
  CONDITION_META,
  CONDITION_VALUES,
  STATUS_META,
  URGENCY_META,
  WARRANTY_META,
  ageLabel,
  categoryIcon,
  healthScoreClassName,
} from '@/components/organisms/equipment/equipmentMeta';
import type { ContactAddress } from '@/types/contact';
import type {
  Equipment,
  EquipmentConditionRating,
  EquipmentFilters,
  EquipmentStatus,
  EquipmentWarrantyStatus,
} from '@/types/equipment';

// NOT: equipment registry CRUD — build a readable label for a customer's service address.
function addressLabel(address: ContactAddress): string {
  const street = address.street1 ?? address.street ?? '';
  const heading = address.title?.trim() || street || 'Address';
  const cityState = [address.city?.trim(), address.state?.trim()].filter(Boolean).join(', ');
  return cityState && cityState !== heading ? `${heading} · ${cityState}` : heading;
}

export const Route = createFileRoute('/_dashboard/dashboard/equipment')({
  beforeLoad: () => {
    const { permissions } = useAuthStore.getState();
    if (permissions.length > 0 && !permissions.includes('equipment:view')) {
      throw redirect({ to: '/dashboard/home' });
    }
  },
  component: EquipmentDashboardPage,
});

const ALL = '__all__';
const STATUS_VALUES: EquipmentStatus[] = ['ACTIVE', 'DECOMMISSIONED', 'REPLACED', 'UNKNOWN'];
const WARRANTY_VALUES: EquipmentWarrantyStatus[] = [
  'ACTIVE',
  'EXPIRING_SOON',
  'EXPIRED',
  'UNKNOWN',
];
const AGE_BANDS: { key: string; label: string; min?: number; max?: number }[] = [
  { key: 'any', label: 'Any age' },
  { key: '0-5', label: '0–5 yrs', min: 0, max: 5 },
  { key: '6-10', label: '6–10 yrs', min: 6, max: 10 },
  { key: '11-15', label: '11–15 yrs', min: 11, max: 15 },
  { key: '16', label: '16+ yrs', min: 16 },
];

function StatCard({
  icon: Icon,
  label,
  value,
  hint,
  tone,
  loading,
}: {
  icon: typeof Package;
  label: string;
  value: number | string;
  hint?: string;
  tone?: string;
  loading?: boolean;
}) {
  return (
    <Card>
      <CardContent className="flex items-start gap-3 p-4">
        <span
          className={cn(
            'flex size-9 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground',
            tone,
          )}
        >
          <Icon className="size-4" />
        </span>
        <div className="min-w-0">
          <p className="text-xs text-muted-foreground">{label}</p>
          {loading ? (
            <Skeleton className="mt-1 h-6 w-12" />
          ) : (
            <p className="text-xl font-semibold tabular-nums">{value}</p>
          )}
          {hint ? <p className="text-xs text-muted-foreground">{hint}</p> : null}
        </div>
      </CardContent>
    </Card>
  );
}

function EquipmentDashboardPage() {
  const canEdit = useHasPermission('equipment:edit');
  const canCreate = useHasPermission('equipment:create');

  const [q, setQ] = useState('');
  const [category, setCategory] = useState<string>(ALL);
  const [status, setStatus] = useState<string>(ALL);
  const [condition, setCondition] = useState<string>(ALL);
  const [warrantyStatus, setWarrantyStatus] = useState<string>(ALL);
  const [ageBand, setAgeBand] = useState<string>('any');

  const [detailId, setDetailId] = useState<string | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);

  // NOT: equipment registry CRUD — create + edit share one EquipmentFormDialog.
  // Edit derives the customer from the selected equipment; create has no ambient
  // contact, so the user picks one first (customerPickerOpen → createCustomerId).
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Equipment | null>(null);
  const [customerPickerOpen, setCustomerPickerOpen] = useState(false);
  const [createCustomerId, setCreateCustomerId] = useState<string | null>(null);

  const categoriesQuery = useEquipmentCategories();
  const inventoryQuery = useEquipmentInventoryReport();
  const warrantyQuery = useEquipmentWarrantyReport();
  const overdueQuery = useMaintenanceOverdue();
  const pipelineQuery = useReplacementPipelineReport();
  const expiringQuery = useWarrantyExpiring();

  const band = AGE_BANDS.find((b) => b.key === ageBand);
  const filters: EquipmentFilters = useMemo(
    () => ({
      q: q.trim() || undefined,
      category: category === ALL ? undefined : category,
      status: status === ALL ? undefined : (status as EquipmentStatus),
      condition: condition === ALL ? undefined : (condition as EquipmentConditionRating),
      warrantyStatus:
        warrantyStatus === ALL ? undefined : (warrantyStatus as EquipmentWarrantyStatus),
      minAge: band?.min,
      maxAge: band?.max,
      limit: 100,
    }),
    [q, category, status, condition, warrantyStatus, band],
  );

  const equipmentQuery = useEquipment(filters);
  const equipment = equipmentQuery.data?.items ?? [];

  function openDetail(id: string) {
    setDetailId(id);
    setDetailOpen(true);
  }

  // NOT: equipment registry CRUD — the form needs a customer + its service
  // addresses. Edit reuses the equipment's customer; create uses the picked one.
  const formCustomerId = editing?.customerId ?? createCustomerId ?? '';
  const formAddressesQuery = useContactAddresses(formCustomerId);
  const formAddresses: EquipmentAddressOption[] = useMemo(
    () =>
      (formAddressesQuery.data ?? [])
        .filter((address): address is ContactAddress & { id: string } => Boolean(address.id))
        .map((address) => ({ id: address.id, label: addressLabel(address) })),
    [formAddressesQuery.data],
  );

  function openEdit(item: Equipment) {
    setCreateCustomerId(null);
    setEditing(item);
    setFormOpen(true);
  }

  function handlePickCustomer(contact: CustomerSelectorContactResult) {
    const id = contact.id ?? contact.contactId ?? contact._id;
    if (!id) return;
    setEditing(null);
    setCreateCustomerId(id);
    setCustomerPickerOpen(false);
    setFormOpen(true);
  }

  function handleFormOpenChange(next: boolean) {
    setFormOpen(next);
    if (!next) {
      setEditing(null);
      setCreateCustomerId(null);
    }
  }

  const categoryOptions = (categoriesQuery.data ?? []).filter((c) => c.status !== 'ARCHIVED');

  return (
    <div className="space-y-6 p-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold">Equipment Inventory</h1>
          <p className="text-sm text-muted-foreground">
            Equipment you service across every customer — condition, warranty, and replacement risk.
          </p>
        </div>
        {canCreate ? (
          <Button className="shrink-0" onClick={() => setCustomerPickerOpen(true)}>
            <Plus className="mr-2 size-4" />
            New equipment
          </Button>
        ) : null}
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-5">
        <StatCard
          icon={Package}
          label="Total equipment"
          value={inventoryQuery.data?.total ?? 0}
          loading={inventoryQuery.isLoading}
        />
        <StatCard
          icon={Boxes}
          label="Under warranty"
          value={warrantyQuery.data?.active ?? 0}
          hint={`${warrantyQuery.data?.expired ?? 0} expired`}
          tone="bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300"
          loading={warrantyQuery.isLoading}
        />
        <StatCard
          icon={CalendarClock}
          label="Warranty expiring"
          value={expiringQuery.data?.total ?? 0}
          hint={expiringQuery.data ? `next ${expiringQuery.data.windowDays} days` : undefined}
          tone="bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300"
          loading={expiringQuery.isLoading}
        />
        <StatCard
          icon={Wrench}
          label="Overdue service"
          value={overdueQuery.data?.total ?? 0}
          hint={overdueQuery.data ? `> ${overdueQuery.data.overdueMonths} mo` : undefined}
          tone="bg-orange-100 text-orange-700 dark:bg-orange-950 dark:text-orange-300"
          loading={overdueQuery.isLoading}
        />
        <StatCard
          icon={ShieldAlert}
          label="Replacement due"
          value={pipelineQuery.data?.total ?? 0}
          tone="bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300"
          loading={pipelineQuery.isLoading}
        />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-[200px] flex-1">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search equipment…"
            className="pl-8"
          />
        </div>
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger className="w-[170px]">
            <SelectValue placeholder="Category" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All categories</SelectItem>
            {categoryOptions.map((c) => (
              <SelectItem key={c.id} value={c.key}>
                {c.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All statuses</SelectItem>
            {STATUS_VALUES.map((value) => (
              <SelectItem key={value} value={value}>
                {STATUS_META[value].label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={condition} onValueChange={setCondition}>
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="Condition" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All conditions</SelectItem>
            {CONDITION_VALUES.map((value) => (
              <SelectItem key={value} value={value}>
                {CONDITION_META[value].label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={warrantyStatus} onValueChange={setWarrantyStatus}>
          <SelectTrigger className="w-[160px]">
            <SelectValue placeholder="Warranty" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All warranties</SelectItem>
            {WARRANTY_VALUES.map((value) => (
              <SelectItem key={value} value={value}>
                {WARRANTY_META[value].label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={ageBand} onValueChange={setAgeBand}>
          <SelectTrigger className="w-[130px]">
            <SelectValue placeholder="Age" />
          </SelectTrigger>
          <SelectContent>
            {AGE_BANDS.map((b) => (
              <SelectItem key={b.key} value={b.key}>
                {b.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Equipment table */}
      <Card>
        <CardContent className="p-0">
          {equipmentQuery.isLoading ? (
            <div className="space-y-2 p-4">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          ) : equipmentQuery.isError ? (
            <div className="flex flex-col items-center gap-3 py-12 text-center">
              <AlertCircle className="size-8 text-destructive" />
              <p className="text-sm text-muted-foreground">Failed to load equipment.</p>
              <Button variant="outline" size="sm" onClick={() => void equipmentQuery.refetch()}>
                <RefreshCw className="mr-2 size-3.5" />
                Retry
              </Button>
            </div>
          ) : equipment.length === 0 ? (
            <div className="flex flex-col items-center gap-2 py-12 text-center">
              <Package className="size-8 text-muted-foreground" />
              <p className="text-sm font-medium">No equipment match these filters</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Equipment</TableHead>
                  <TableHead>Equipment type</TableHead>
                  <TableHead>Condition</TableHead>
                  <TableHead>Warranty</TableHead>
                  <TableHead>Age</TableHead>
                  <TableHead>Health</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {equipment.map((equipment: Equipment) => {
                  const Icon = categoryIcon(equipment.category);
                  return (
                    <TableRow
                      key={equipment.id}
                      className="cursor-pointer"
                      onClick={() => openDetail(equipment.id)}
                    >
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <span className="flex size-7 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
                            <Icon className="size-3.5" />
                          </span>
                          <div className="min-w-0">
                            <p className="truncate text-sm font-medium">{equipment.name}</p>
                            <p className="truncate text-xs text-muted-foreground">
                              {equipment.equipmentNumber}
                            </p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <p className="text-sm font-medium">
                          {equipment.equipmentType?.name ?? equipment.category}
                        </p>
                        {equipment.equipmentType?.brand || equipment.equipmentType?.model ? (
                          <p className="text-xs text-muted-foreground">
                            {[equipment.equipmentType?.brand, equipment.equipmentType?.model]
                              .filter(Boolean)
                              .join(' · ')}
                          </p>
                        ) : null}
                      </TableCell>
                      <TableCell>
                        <Badge className={cn(CONDITION_META[equipment.condition].className)}>
                          {CONDITION_META[equipment.condition].label}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge
                          className={cn(
                            WARRANTY_META[equipment.warranty.manufacturerStatus].className,
                          )}
                        >
                          {WARRANTY_META[equipment.warranty.manufacturerStatus].label}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {ageLabel(equipment.ageYears) ?? '—'}
                      </TableCell>
                      <TableCell>
                        {equipment.healthScore != null ? (
                          <span
                            className={cn(
                              'text-sm font-semibold tabular-nums',
                              healthScoreClassName(equipment.healthScore),
                            )}
                          >
                            {equipment.healthScore}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge className={cn(STATUS_META[equipment.status].className)}>
                          {STATUS_META[equipment.status].label}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Outreach lists */}
      <div className="grid gap-4 lg:grid-cols-2">
        <OutreachList
          title="Warranty expiring soon"
          icon={CalendarClock}
          loading={expiringQuery.isLoading}
          error={expiringQuery.isError}
          onRetry={() => void expiringQuery.refetch()}
          items={(expiringQuery.data?.items ?? []).map((equipment) => ({
            id: equipment.id,
            primary: equipment.name,
            secondary: equipment.equipmentType?.name ?? equipment.category,
            trailing: equipment.warranty.manufacturerEnd
              ? formatDateOnly(equipment.warranty.manufacturerEnd)
              : '—',
          }))}
          onOpen={openDetail}
          emptyLabel="No warranties expiring in the window."
        />
        <OutreachList
          title="Maintenance overdue"
          icon={Wrench}
          loading={overdueQuery.isLoading}
          error={overdueQuery.isError}
          onRetry={() => void overdueQuery.refetch()}
          items={(overdueQuery.data?.items ?? []).map((equipment) => ({
            id: equipment.id,
            primary: equipment.name,
            secondary: equipment.equipmentType?.name ?? equipment.category,
            trailing: equipment.lastServicedAt
              ? `Last ${formatDateOnly(equipment.lastServicedAt.slice(0, 10))}`
              : 'Never serviced',
          }))}
          onOpen={openDetail}
          emptyLabel="Everything is on schedule."
        />
      </div>

      {/* Replacement pipeline */}
      <Card>
        <CardContent className="p-0">
          <div className="flex items-center gap-2 border-b px-4 py-3">
            <ShieldAlert className="size-4 text-muted-foreground" />
            <h3 className="text-sm font-semibold">Replacement pipeline</h3>
          </div>
          {pipelineQuery.isLoading ? (
            <div className="space-y-2 p-4">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-9 w-full" />
              ))}
            </div>
          ) : (pipelineQuery.data?.items.length ?? 0) === 0 ? (
            <p className="p-6 text-center text-sm text-muted-foreground">
              No equipment past their expected lifespan.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Equipment</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Age</TableHead>
                  <TableHead>Past EOL</TableHead>
                  <TableHead>Urgency</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(pipelineQuery.data?.items ?? []).map((item) => (
                  <TableRow
                    key={item.id}
                    className="cursor-pointer"
                    onClick={() => openDetail(item.id)}
                  >
                    <TableCell>
                      <p className="text-sm font-medium">{item.name}</p>
                      <p className="text-xs text-muted-foreground">{item.equipmentNumber}</p>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">{item.category}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {ageLabel(item.ageYears) ?? '—'}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {item.yearsPastEndOfLife > 0 ? `${item.yearsPastEndOfLife} yr` : 'Due'}
                    </TableCell>
                    <TableCell>
                      <Badge
                        className={cn(URGENCY_META[item.replacementUrgency ?? 'NONE'].className)}
                      >
                        {URGENCY_META[item.replacementUrgency ?? 'NONE'].label}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <EquipmentDetailSheet
        equipmentId={detailId}
        open={detailOpen}
        onOpenChange={setDetailOpen}
        canEdit={canEdit}
        onEdit={(item) => {
          setDetailOpen(false);
          openEdit(item);
        }}
      />

      {/* NOT: equipment registry CRUD — pick the customer for a brand-new record. */}
      <Dialog open={customerPickerOpen} onOpenChange={setCustomerPickerOpen}>
        <DialogContent className="flex h-[560px] flex-col overflow-hidden sm:max-w-md">
          <DialogHeader className="shrink-0">
            <DialogTitle>Add equipment</DialogTitle>
            <DialogDescription>Choose the customer this equipment belongs to.</DialogDescription>
          </DialogHeader>
          <div className="min-h-0 flex-1 overflow-y-auto [scrollbar-gutter:stable]">
            <CompanyLocationSelector
              selected={null}
              onSelect={handlePickCustomer}
              onClear={() => {}}
              placeholder="Search customers by name, email, or phone…"
              resultsLayout="inline"
            />
          </div>
        </DialogContent>
      </Dialog>

      {/* NOT: equipment registry CRUD — shared create + edit form. */}
      <EquipmentFormDialog
        open={formOpen}
        onOpenChange={handleFormOpenChange}
        customerId={formCustomerId}
        addresses={formAddresses}
        editing={editing}
      />
    </div>
  );
}

// ─── Outreach list card ───────────────────────────────────────────────────────

function OutreachList({
  title,
  icon: Icon,
  loading,
  error,
  onRetry,
  items,
  onOpen,
  emptyLabel,
}: {
  title: string;
  icon: typeof Wrench;
  loading: boolean;
  error: boolean;
  onRetry: () => void;
  items: { id: string; primary: string; secondary: string; trailing: string }[];
  onOpen: (id: string) => void;
  emptyLabel: string;
}) {
  return (
    <Card>
      <CardContent className="p-0">
        <div className="flex items-center gap-2 border-b px-4 py-3">
          <Icon className="size-4 text-muted-foreground" />
          <h3 className="text-sm font-semibold">{title}</h3>
          {!loading && !error ? (
            <Badge variant="outline" className="ml-auto">
              {items.length}
            </Badge>
          ) : null}
        </div>
        {loading ? (
          <div className="space-y-2 p-4">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-9 w-full" />
            ))}
          </div>
        ) : error ? (
          <div className="flex flex-col items-center gap-2 py-8 text-center">
            <AlertCircle className="size-6 text-destructive" />
            <Button variant="ghost" size="sm" onClick={onRetry}>
              <RefreshCw className="mr-1 size-3.5" />
              Retry
            </Button>
          </div>
        ) : items.length === 0 ? (
          <p className="p-6 text-center text-sm text-muted-foreground">{emptyLabel}</p>
        ) : (
          <ul className="divide-y">
            {items.slice(0, 8).map((item) => (
              <li key={item.id}>
                <button
                  type="button"
                  onClick={() => onOpen(item.id)}
                  className="flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors hover:bg-muted/50"
                >
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">{item.primary}</p>
                    <p className="truncate text-xs text-muted-foreground">{item.secondary}</p>
                  </div>
                  <span className="shrink-0 text-xs text-muted-foreground">{item.trailing}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
